// ===========================================
// HOOKS: Notificaciones In-App (CORE)
// ===========================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { useSupabase } from '@/lib/supabase-context'
import { notificacionesService } from '../services/notificacionesService'
import type { FiltrosNotificaciones, NotificacionInsert } from '../types/notificaciones'

// ===========================================
// Query Keys
// ===========================================

export const notificacionesKeys = {
  all: ['notificaciones'] as const,
  lista: (email: string, filtros?: FiltrosNotificaciones) =>
    [...notificacionesKeys.all, 'lista', email, filtros] as const,
  noLeidas: (email: string) => [...notificacionesKeys.all, 'no-leidas', email] as const,
  conteo: (email: string) => [...notificacionesKeys.all, 'conteo', email] as const,
  resumen: (email: string) => [...notificacionesKeys.all, 'resumen', email] as const,
}

// ===========================================
// Hooks de Query
// ===========================================

/**
 * Lista notificaciones del usuario actual
 */
export function useNotificaciones(filtros?: FiltrosNotificaciones) {
  const { user } = useAuth0()
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: notificacionesKeys.lista(user?.email || '', filtros),
    queryFn: () => notificacionesService.listar(supabase!, user?.email || '', filtros),
    enabled: !!user?.email && !!supabase && !supabaseLoading,
    refetchInterval: 5 * 60 * 1000, // Refrescar cada 5 minutos (reducido de 30seg)
  })
}

/**
 * Obtiene notificaciones no leidas
 */
export function useNotificacionesNoLeidas(limite = 20) {
  const { user } = useAuth0()
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: notificacionesKeys.noLeidas(user?.email || ''),
    queryFn: () => notificacionesService.obtenerNoLeidas(supabase!, user?.email || '', limite),
    enabled: !!user?.email && !!supabase && !supabaseLoading,
    refetchInterval: 2 * 60 * 1000, // Refrescar cada 2 minutos (reducido de 15seg)
  })
}

/**
 * Cuenta notificaciones no leidas (para badge)
 */
export function useConteoNotificaciones() {
  const { user } = useAuth0()
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: notificacionesKeys.conteo(user?.email || ''),
    queryFn: () => notificacionesService.contarNoLeidas(supabase!, user?.email || ''),
    enabled: !!user?.email && !!supabase && !supabaseLoading,
    refetchInterval: 2 * 60 * 1000, // Refrescar cada 2 minutos (reducido de 15seg)
  })
}

/**
 * Obtiene resumen de notificaciones
 */
export function useResumenNotificaciones() {
  const { user } = useAuth0()
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: notificacionesKeys.resumen(user?.email || ''),
    queryFn: () => notificacionesService.obtenerResumen(supabase!, user?.email || ''),
    enabled: !!user?.email && !!supabase && !supabaseLoading,
    refetchInterval: 60 * 1000, // Refrescar cada minuto
  })
}

// ===========================================
// Hooks de Mutation
// ===========================================

/**
 * Crea una notificacion
 */
export function useCrearNotificacion() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()

  return useMutation({
    mutationFn: (notificacion: NotificacionInsert) => {
      if (!supabase) throw new Error('Supabase no inicializado')
      return notificacionesService.crear(supabase, notificacion)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: notificacionesKeys.lista(variables.user_email),
      })
      queryClient.invalidateQueries({
        queryKey: notificacionesKeys.conteo(variables.user_email),
      })
    },
  })
}

/**
 * Marca una notificacion como leida
 */
export function useMarcarLeida() {
  const { user } = useAuth0()
  const { supabase } = useSupabase()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => {
      if (!supabase) throw new Error('Supabase no inicializado')
      return notificacionesService.marcarLeida(supabase, id)
    },
    onSuccess: () => {
      if (user?.email) {
        queryClient.invalidateQueries({
          queryKey: notificacionesKeys.all,
        })
      }
    },
  })
}

/**
 * Marca todas las notificaciones como leidas
 */
export function useMarcarTodasLeidas() {
  const { user } = useAuth0()
  const { supabase } = useSupabase()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => {
      if (!supabase) throw new Error('Supabase no inicializado')
      if (!user?.email) throw new Error('Usuario no autenticado')
      return notificacionesService.marcarTodasLeidas(supabase, user.email)
    },
    onSuccess: () => {
      if (user?.email) {
        queryClient.invalidateQueries({
          queryKey: notificacionesKeys.all,
        })
      }
    },
  })
}

/**
 * Elimina una notificacion
 */
export function useEliminarNotificacion() {
  const { user } = useAuth0()
  const { supabase } = useSupabase()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => {
      if (!supabase) throw new Error('Supabase no inicializado')
      return notificacionesService.eliminar(supabase, id)
    },
    onSuccess: () => {
      if (user?.email) {
        queryClient.invalidateQueries({
          queryKey: notificacionesKeys.all,
        })
      }
    },
  })
}

/**
 * Limpia notificaciones antiguas
 */
export function useLimpiarNotificacionesAntiguas() {
  const { user } = useAuth0()
  const { supabase } = useSupabase()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dias?: number) => {
      if (!supabase) throw new Error('Supabase no inicializado')
      if (!user?.email) throw new Error('Usuario no autenticado')
      return notificacionesService.limpiarAntiguas(supabase, user.email, dias ?? 30)
    },
    onSuccess: () => {
      if (user?.email) {
        queryClient.invalidateQueries({
          queryKey: notificacionesKeys.all,
        })
      }
    },
  })
}

// NOTA (ORR-69): el hook `useNotificador` fue eliminado junto con los 15
// metodos legacy del service. No tenia callers reales — solo envolvia
// helpers zombis. Usar `notificacionesService.notificar()` directamente
// con origen trazable para crear notificaciones desde hooks/mutations.

// Re-export tipos para conveniencia
export type { Notificacion, NotificacionInsert, FiltrosNotificaciones, TipoNotificacion, ResumenNotificaciones } from '../types/notificaciones'
export { TIPO_NOTIFICACION_CONFIG, tiempoRelativo, PLANTILLAS_NOTIFICACION } from '../types/notificaciones'
